/**
 * Inferrex UI Constants
 *
 * Variant definitions for shared primitives that reference design tokens.
 * All values resolve to CSS custom properties from packages/ui/src/tokens.css.
 */

export const BADGE_VARIANTS = {
  default:
    'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
  secondary:
    'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
  destructive:
    'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
  outline: 'text-foreground',
  success:
    'border-transparent bg-[var(--success-bg)] text-[var(--success)]',
  warning:
    'border-transparent bg-[var(--warning-bg)] text-[var(--warning)]',
  info:
    'border-transparent bg-[var(--info-bg)] text-[var(--info)]',

  /* Sync status */
  syncActive:
    'border-transparent bg-[var(--sync-active-bg)] text-[var(--sync-active)]',
  syncIdle:
    'border-transparent bg-[var(--sync-idle-bg)] text-[var(--sync-idle)]',
  syncError:
    'border-transparent bg-[var(--sync-error-bg)] text-[var(--sync-error)]',
  syncPaused:
    'border-transparent bg-[var(--sync-paused-bg)] text-[var(--sync-paused)]',
  syncQueued:
    'border-transparent bg-[var(--sync-queued-bg)] text-[var(--sync-queued)]',

  /* Healing tiers */
  healingAuto:
    'border-transparent bg-[var(--healing-auto-bg)] text-[var(--healing-auto)]',
  healingAi:
    'border-transparent bg-[var(--healing-ai-bg)] text-[var(--healing-ai)]',
  healingHuman:
    'border-transparent bg-[var(--healing-human-bg)] text-[var(--healing-human)]',

  /* Confidence */
  confidenceHigh:
    'border-transparent bg-[var(--confidence-high-bg)] text-[var(--confidence-high)]',
  confidenceMedium:
    'border-transparent bg-[var(--confidence-medium-bg)] text-[var(--confidence-medium)]',
  confidenceLow:
    'border-transparent bg-[var(--confidence-low-bg)] text-[var(--confidence-low)]',

  /* Trust tiers */
  trustPlatinum:
    'border-transparent bg-[var(--trust-platinum-bg)] text-[var(--trust-platinum)]',
  trustGold:
    'border-transparent bg-[var(--trust-gold-bg)] text-[var(--trust-gold)]',
  trustSilver:
    'border-transparent bg-[var(--trust-silver-bg)] text-[var(--trust-silver)]',
  trustBronze:
    'border-transparent bg-[var(--trust-bronze-bg)] text-[var(--trust-bronze)]',

  /* Billing status */
  billingActive:
    'border-transparent bg-[var(--billing-active-bg)] text-[var(--billing-active)]',
  billingTrial:
    'border-transparent bg-[var(--billing-trial-bg)] text-[var(--billing-trial)]',
  billingPastDue:
    'border-transparent bg-[var(--billing-past-due-bg)] text-[var(--billing-past-due)]',
  billingSuspended:
    'border-transparent bg-[var(--billing-suspended-bg)] text-[var(--billing-suspended)]',
  billingCancelled:
    'border-transparent bg-[var(--billing-cancelled-bg)] text-[var(--billing-cancelled)]',

  /* Tier badges */
  tierPilot:
    'border-transparent bg-[var(--tier-pilot-bg)] text-[var(--tier-pilot)]',
  tierStarter:
    'border-transparent bg-[var(--tier-starter-bg)] text-[var(--tier-starter)]',
  tierGrowth:
    'border-transparent bg-[var(--tier-growth-bg)] text-[var(--tier-growth)]',
  tierScale:
    'border-transparent bg-[var(--tier-scale-bg)] text-[var(--tier-scale)]',
  tierEnterprise:
    'border-transparent bg-[var(--tier-enterprise-bg)] text-[var(--tier-enterprise)]',
  tierSovereign:
    'border-transparent bg-[var(--tier-sovereign-bg)] text-[var(--tier-sovereign)]',

  /* Priority */
  priorityLow:
    'border-transparent bg-[var(--priority-low-bg)] text-[var(--priority-low)]',
  priorityNormal:
    'border-transparent bg-[var(--priority-normal-bg)] text-[var(--priority-normal)]',
  priorityHigh:
    'border-transparent bg-[var(--priority-high-bg)] text-[var(--priority-high)]',
  priorityUrgent:
    'border-transparent bg-[var(--priority-urgent-bg)] text-[var(--priority-urgent)]',

  /* Overage */
  overageEnabled:
    'border-transparent bg-[var(--overage-enabled-bg)] text-[var(--overage-enabled)]',
  overageAccruing:
    'border-transparent bg-[var(--overage-accruing-bg)] text-[var(--overage-accruing)]',
  overageCapped:
    'border-transparent bg-[var(--overage-capped-bg)] text-[var(--overage-capped)]',
} as const;

// Named `BadgeVariantKey` (not `BadgeVariant`) so it never collides with
// config/types' `BadgeVariant` in the generated root barrel — this is the
// comprehensive ~40-key set; config/types' is the small status subset.
export type BadgeVariantKey = keyof typeof BADGE_VARIANTS;
